import React, { useEffect, useState } from 'react';
import { adminGetAnalytics } from '../../api';

const s = {
    h1:    { fontSize: 22, fontWeight: 700, marginBottom: 4 },
    sub:   { color: '#6b7280', fontSize: 13, marginBottom: 24 },
    cards: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 },
    card:  { background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
    cardN: { fontSize: 28, fontWeight: 800, color: '#1a1d23' },
    cardL: { fontSize: 12, color: '#6b7280', marginTop: 4 },
    grid:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 },
    box:   { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
    boxH:  { fontSize: 15, fontWeight: 700, marginBottom: 16, color: '#1a1d23' },
    row:   { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 },
    bar:   (pct, color) => ({ height: 6, borderRadius: 3, background: '#f3f4f6', flex: 1, marginLeft: 12, position: 'relative', overflow: 'hidden' }),
    fill:  (pct, color) => ({ position: 'absolute', left: 0, top: 0, height: '100%', width: `${pct}%`, background: color || '#6366f1', borderRadius: 3 }),
    pills: { display: 'flex', gap: 6, marginBottom: 20 },
    pill:  (active) => ({
        padding: '6px 14px', borderRadius: 20, border: '1px solid #d1d5db', cursor: 'pointer', fontSize: 12, fontWeight: 600,
        background: active ? '#6366f1' : '#fff', color: active ? '#fff' : '#374151',
    }),
};

const DEVICE_COLORS = { desktop: '#6366f1', mobile: '#10b981', tablet: '#f59e0b', bot: '#9ca3af', unknown: '#d1d5db' };

function MiniChart({ data, maxVal }) {
    if (!data.length) return <div style={{ color: '#9ca3af', fontSize: 12 }}>No data yet</div>;
    const max = maxVal || Math.max(...data.map(d => d.views), 1);
    return (
        <div style={{ display: 'flex', alignItems: 'end', gap: 2, height: 80 }}>
            {data.map((d, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                        width: '100%', minWidth: 4, background: '#6366f1', borderRadius: '3px 3px 0 0',
                        height: Math.max(2, (d.views / max) * 70),
                    }} title={`${d.day || d.hour}: ${d.views} views`} />
                </div>
            ))}
        </div>
    );
}

function HourlyChart({ data }) {
    // Fill all 24 hours
    const hours = Array.from({ length: 24 }, (_, i) => {
        const found = data.find(d => parseInt(d.hour) === i);
        return { hour: i, views: found ? parseInt(found.views) : 0 };
    });
    const max = Math.max(...hours.map(h => h.views), 1);
    return (
        <div style={{ display: 'flex', alignItems: 'end', gap: 1, height: 70 }}>
            {hours.map(h => (
                <div key={h.hour} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                        width: '100%', minWidth: 3, background: h.views > 0 ? '#10b981' : '#f3f4f6',
                        borderRadius: '2px 2px 0 0', height: Math.max(2, (h.views / max) * 60),
                    }} title={`${h.hour}:00 — ${h.views} views`} />
                    {h.hour % 6 === 0 && <span style={{ fontSize: 9, color: '#9ca3af', marginTop: 2 }}>{h.hour}h</span>}
                </div>
            ))}
        </div>
    );
}

export default function AdminAnalytics() {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        adminGetAnalytics(days)
            .then(r => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [days]);

    if (loading || !data) return <div>Loading analytics...</div>;

    const { today, views_per_day, top_pages, top_referrers, devices, hourly } = data;
    const totalViews = views_per_day.reduce((s, d) => s + parseInt(d.views), 0);
    const totalUniques = views_per_day.reduce((s, d) => s + parseInt(d.unique_visitors), 0);
    const maxPageViews = top_pages.length ? parseInt(top_pages[0].views) : 1;

    return (
        <div>
            <h1 style={s.h1}>Analytics</h1>
            <div style={s.sub}>Site traffic and visitor insights</div>

            {/* Period selector */}
            <div style={s.pills}>
                {[7, 14, 30, 90].map(d => (
                    <span key={d} style={s.pill(days === d)} onClick={() => setDays(d)}>
                        {d}d
                    </span>
                ))}
            </div>

            {/* Summary cards */}
            <div className="og-grid-4" style={s.cards}>
                <div style={s.card}>
                    <div style={s.cardN}>{parseInt(today.views || 0)}</div>
                    <div style={s.cardL}>Views today</div>
                </div>
                <div style={s.card}>
                    <div style={s.cardN}>{parseInt(today.unique_visitors || 0)}</div>
                    <div style={s.cardL}>Unique visitors today</div>
                </div>
                <div style={s.card}>
                    <div style={s.cardN}>{totalViews}</div>
                    <div style={s.cardL}>Views ({days}d)</div>
                </div>
                <div style={s.card}>
                    <div style={s.cardN}>{totalUniques}</div>
                    <div style={s.cardL}>Unique visitors ({days}d)</div>
                </div>
            </div>

            {/* Charts row */}
            <div className="og-grid-charts" style={s.grid}>
                {/* Daily views chart */}
                <div style={s.box}>
                    <div style={s.boxH}>Daily Views</div>
                    <MiniChart data={views_per_day} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                        <span style={{ fontSize: 10, color: '#9ca3af' }}>{views_per_day[0]?.day?.slice(5) || ''}</span>
                        <span style={{ fontSize: 10, color: '#9ca3af' }}>{views_per_day[views_per_day.length - 1]?.day?.slice(5) || ''}</span>
                    </div>
                </div>

                {/* Hourly today */}
                <div style={s.box}>
                    <div style={s.boxH}>Today by Hour</div>
                    <HourlyChart data={hourly} />
                </div>
            </div>

            <div style={s.grid}>
                {/* Top pages */}
                <div style={s.box}>
                    <div style={s.boxH}>Top Pages</div>
                    {top_pages.map((p, i) => (
                        <div key={i} style={s.row}>
                            <span style={{ fontFamily: 'monospace', color: '#374151', flex: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {p.path}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                <div style={s.bar()}>
                                    <div style={s.fill(parseInt(p.views) / maxPageViews * 100)} />
                                </div>
                            </div>
                            <span style={{ minWidth: 50, textAlign: 'right', fontWeight: 600 }}>{p.views}</span>
                        </div>
                    ))}
                    {!top_pages.length && <div style={{ color: '#9ca3af', fontSize: 13 }}>No data yet</div>}
                </div>

                {/* Right column: devices + referrers */}
                <div>
                    {/* Devices */}
                    <div style={{ ...s.box, marginBottom: 20 }}>
                        <div style={s.boxH}>Devices</div>
                        {devices.map((d, i) => (
                            <div key={i} style={{ ...s.row, alignItems: 'center' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: DEVICE_COLORS[d.device_type] || '#ccc', display: 'inline-block' }} />
                                    {d.device_type}
                                </span>
                                <span style={{ fontWeight: 600 }}>{d.views}</span>
                            </div>
                        ))}
                        {!devices.length && <div style={{ color: '#9ca3af', fontSize: 13 }}>No data yet</div>}
                    </div>

                    {/* Referrers */}
                    <div style={s.box}>
                        <div style={s.boxH}>Top Referrers</div>
                        {top_referrers.map((r, i) => (
                            <div key={i} style={s.row}>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#374151' }}>
                                    {r.referrer ? new URL(r.referrer).hostname : '(direct)'}
                                </span>
                                <span style={{ fontWeight: 600, marginLeft: 8 }}>{r.views}</span>
                            </div>
                        ))}
                        {!top_referrers.length && <div style={{ color: '#9ca3af', fontSize: 13 }}>No data yet — referrers appear when visitors come from external links</div>}
                    </div>
                </div>
            </div>

            {/* Bots count */}
            {parseInt(today.bots) > 0 && (
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 12 }}>
                    {today.bots} bot requests filtered from stats today
                </div>
            )}
        </div>
    );
}
